import { z } from 'zod';
import type { PracticeBuiltArm } from '../practice-builds/schema';
import type { TileCopy } from '../journey/schema';

/**
 * `source_meta.prompt_params` — a template-level, PHI-free disclosure of the
 * system prompt a conversation ran under, plus the practice that governed it.
 *
 * Deliberately NOT the rendered prompt. A rendered prompt embeds
 * `member_context_block`, `past_memories` and `previous_conversations_block`,
 * making it more PHI-dense than the transcript itself; that would move the
 * ADR-098 egress boundary and needs a compliance decision first. Emitting
 * committed template text with every variable left as a placeholder moves no
 * boundary: section text is repo content and the practice fields are
 * clinician-signed catalogue text plus three consciously-allowed member-derived
 * strings (see `PRACTICE_DISCLOSURE`).
 *
 * TWO-LAYER VALIDATION. This schema is strict and is the producer's gate: a
 * payload that does not satisfy it is dropped rather than emitted.
 * `ingestionSourceMetaSchema.prompt_params` (see `./schema.ts`) stays
 * `z.record(z.unknown())` on purpose — it mirrors `pangea-ingestion.schema.json`,
 * a byte-for-byte vendored copy of Pangea's contract, and tightening the mirror
 * to OUR disclosure shape would break the conformance guard that keeps the
 * integration faithful rather than a private fork.
 */

export const PROMPT_PARAMS_SPEC_VERSION = 'prompt-params.v1';

/**
 * Which disclosure level the payload carries.
 *
 * `template` is the full disclosure: every prompt section plus the governing
 * practice. `practice_only` is the minimal payload emitted when the prompt-level
 * disclosure is withheld (no audit row, retired definition, prompt moved since
 * the call) but the practice that governed the conversation still resolves —
 * a template edit says nothing about which practice ran, so hiding the practice
 * behind the prompt-moved guard would withhold a fact that is still true.
 */
export const PROMPT_PARAMS_DISCLOSURE = 'template';
export const PROMPT_PARAMS_PRACTICE_ONLY_DISCLOSURE = 'practice_only';

export const promptParamsSectionSchema = z
  .object({
    name: z.string().min(1),
    kind: z.enum(['shared_section', 'agent_body', 'separator']),
    text: z.string(),
    /** SHA-256 of `text`; never comparable to the audit row's rendered-prompt hash. */
    sha256: z.string().regex(/^[0-9a-f]{64}$/),
  })
  .strict();
export type PromptParamsSection = z.infer<typeof promptParamsSectionSchema>;

/**
 * The practice's identity. One field on the wire so a reader needs no branch
 * to find it, but its KEY SPACE is pathway-scoped and a consumer must not
 * treat it as a single foreign key:
 *
 * - `journey`: the composed practice ROW id (its table's primary key) — joins
 *   to the row, the session that launched it, and the outcome recorded
 *   against it.
 * - `base`: the built arm's own id, minted by `build_practice` at construction.
 *   No table has it as a column; it lives inside the stored decision's
 *   response payload and on the arm the voice model received. Its join is to
 *   the same conversation's `build_practice` tool card (which exports that
 *   arm), so the metadata panel's "governing practice" and the transcript's
 *   build call name the same object. The decision row's own primary key is
 *   deliberately not disclosed here: it would not match the tool card.
 */
const practiceIdentityFields = {
  practice_id: z.string().min(1),
} as const;

/** The clinician-signed catalogue fields, identical on both pathways. */
const signedPracticeFields = {
  technique: z.string(),
  technique_version: z.string(),
  intent: z.string(),
  instructions: z.string(),
  never: z.string(),
  done_when: z.string(),
  /** Nullable by contract: a technique with a compositional opening has no signed opener. */
  first_move: z.string().nullable(),
} as const;

/**
 * The practice fields, exactly `PRACTICE_DISCLOSURE` — discriminated ON the
 * pathway rather than sharing one shape with optional extras.
 *
 * The discriminant values are the canonical pathway identifiers, not a parallel
 * vocabulary: ADR-114 Rule 4 fixes `journey` and `base` as the ONLY machine
 * identifiers for the pathways, and the base decision row this reads from
 * already carries them. `prompt-params.test.ts` pins the union's discriminants
 * against `PRACTICE_BUILD_PATHWAY` so a rename breaks there rather than drifts.
 *
 * This is the producer's fail-closed gate, so the pathway-honest field set has
 * to be enforced HERE and not only in the projection that happens to feed it.
 * With `title`/`hook` merely optional, `{ source: 'base', title, hook }` and
 * `{ source: 'journey' }` without them both validated: the projection could not
 * produce either, but the gate would have blessed them from any other producer
 * or a future code path. `title`/`hook` exist only on the journey pathway (they
 * come from the composed practice's tile copy, and the base pathway's stored arm
 * is `.strict()` and rejects a `title` outright), so on `journey` they are
 * REQUIRED and on `base` they are forbidden.
 */
export const promptParamsPracticeSchema = z.discriminatedUnion('source', [
  z
    .object({ source: z.literal('base'), ...practiceIdentityFields, ...signedPracticeFields })
    .strict(),
  z
    .object({
      source: z.literal('journey'),
      ...practiceIdentityFields,
      ...signedPracticeFields,
      title: z.string(),
      hook: z.string(),
    })
    .strict(),
]);
export type PromptParamsPractice = z.infer<typeof promptParamsPracticeSchema>;

export const promptParamsTemplateSchema = z
  .object({
    spec_version: z.literal(PROMPT_PARAMS_SPEC_VERSION),
    disclosure: z.literal(PROMPT_PARAMS_DISCLOSURE),
    /** Call-time provenance, read from the prompt-audit row — never today's registry. */
    agent_definition_id: z.string().min(1),
    agent_definition_version: z.string().min(1),
    prompt_agent: z.string().min(1),
    prompt_version: z.string().min(1),
    sections: z.array(promptParamsSectionSchema),
    /**
     * Sections withheld from this view — always empty today. Retained so a
     * reviewer can distinguish "this agent has no memory slot" (absent from
     * `sections`) from "memory was withheld from this view" (named here).
     */
    omitted: z.array(z.string()),
    /** The variable NAMES the call substituted. Names only; never a value. */
    variable_keys: z.array(z.string()),
    /**
     * The only resolved variables — a CLOSED shape, not an open record.
     *
     * Two independent narrowings, because each blocks a different leak. The
     * value type is boolean, so no rendered string can occupy the slot. The
     * KEY set is enumerated and `.strict()`, so no other variable can appear
     * even as a boolean — an open record would have admitted, say,
     * `risk_signal: true`, which names one of the most clinically sensitive
     * values in the system. Disclosing another variable is therefore a
     * deliberate schema change, reviewed here, rather than a caller's choice.
     */
    resolved: z
      .object({
        /**
         * Journey only: true when the member had no prior journey
         * conversation (a session where they spoke) before this one.
         */
        first_ever: z.boolean().optional(),
      })
      .strict(),
    practice: promptParamsPracticeSchema.optional(),
  })
  .strict();
export type PromptParamsTemplate = z.infer<typeof promptParamsTemplateSchema>;

/**
 * The practice-only payload: exactly the version, the discriminant, and the
 * practice. No provenance, sections, variable names or resolved values — those
 * are prompt-level facts, and this shape exists precisely because they were
 * withheld. `.strict()` so a producer cannot smuggle a prompt-level field onto
 * the shape the prompt-moved guard refused, and `practice` is REQUIRED: an
 * absent practice is the original `absent_*` outcome, never an empty payload.
 */
export const promptParamsPracticeOnlySchema = z
  .object({
    spec_version: z.literal(PROMPT_PARAMS_SPEC_VERSION),
    disclosure: z.literal(PROMPT_PARAMS_PRACTICE_ONLY_DISCLOSURE),
    practice: promptParamsPracticeSchema,
  })
  .strict();
export type PromptParamsPracticeOnly = z.infer<typeof promptParamsPracticeOnlySchema>;

/**
 * The producer's gate: one of the two disclosure shapes, discriminated on
 * `disclosure` rather than inferred from which keys happen to be present.
 * Both live under `prompt-params.v1` — a reader keyed on `practice.*` sees the
 * same block on either shape, so the addition is purely additive.
 */
export const promptParamsSchema = z.discriminatedUnion('disclosure', [
  promptParamsTemplateSchema,
  promptParamsPracticeOnlySchema,
]);
export type PromptParams = z.infer<typeof promptParamsSchema>;

/**
 * Which practice fields are disclosed, split by what they are rather than by
 * where they came from — so the decision is legible in one place and reversible
 * as a one-field change.
 *
 * `assemblePractice` is pure template fill with no model call, and `intent` is
 * never interpolated into the other fields, so the split is exact.
 *
 * The member-derived three are included deliberately:
 *   - base `intent` paraphrases something the member said minutes earlier in
 *     this same conversation, whose verbatim turns already egress in
 *     `transcript` — strictly less disclosive than what already ships.
 *   - journey `intent` is composed ahead of the call and may reference material
 *     outside this transcript: a genuine but one-sentence widening, consciously
 *     accepted, and far smaller than `member_context_block`.
 *   - `title`/`hook` are composer-written with member context in hand but are
 *     already shown to the member on their home-screen card, and without them
 *     the practice loses the framing the session was built around.
 *
 * NOT disclosed anywhere: `member_context_block`, `past_memories`,
 * `previous_conversations_block`, and every other template-variable VALUE —
 * those carry data from outside the conversation, which is what would actually
 * widen the boundary.
 */
export const PRACTICE_DISCLOSURE = {
  /** Clinician-signed catalogue text (ADR-109). */
  always: ['technique', 'technique_version', 'instructions', 'never', 'done_when', 'first_move'],
  /** Member-derived, included per the rationale above. `title`/`hook` are journey-only. */
  memberDerived: ['intent', 'title', 'hook'],
  /**
   * Opaque identity — a UUID, no content. Journey discloses the composed
   * practice ROW id (the key Pangea can join back on), base the built arm's own
   * id; `anchor`, the selection rationale and `source_tier` stay undisclosed.
   */
  identity: ['practice_id'],
} as const;

/**
 * A discriminated union rather than an optional `tileCopy`, so the pathway
 * decides the field set at the type level: `base` cannot carry tile copy (it
 * has none, and emitting `title`/`hook` there would fabricate a field set the
 * pathway does not have), and `journey` cannot omit it.
 */
export type ProjectPracticeDisclosureInput =
  | {
      source: 'base';
      /** The base decision's stored response payload. */
      arm: PracticeBuiltArm;
    }
  | {
      source: 'journey';
      /**
       * The composed practice's ROW id (its primary key) — deliberately not
       * the stored arm's own `practice_id`, which `build_practice` mints at
       * composition time and which nothing outside the row can join on.
       */
      practiceId: string;
      /** The composed practice's stored arm. */
      arm: PracticeBuiltArm;
      /** The composed practice's tile copy — journey-only, and required here. */
      tileCopy: TileCopy;
    };

/**
 * Project a stored practice onto the disclosed field set.
 *
 * Field-by-field on purpose — never a spread or rest of the stored row. A spread
 * would silently start disclosing whatever a future column adds (`anchor`, a
 * selection rationale, a source tier), turning an egress decision
 * into an accident. Field names keep the stored arm's flat `snake_case` under
 * the ADR-066 Rule 3 carve-out, so the disclosure matches the stored arm rather
 * than re-casing clinician-signed text.
 */
export function projectPracticeDisclosure(
  input: ProjectPracticeDisclosureInput,
): PromptParamsPractice {
  const { source, arm } = input;
  const signed = {
    technique: arm.technique,
    technique_version: arm.technique_version,
    intent: arm.intent,
    instructions: arm.instructions,
    never: arm.never,
    done_when: arm.done_when,
    first_move: arm.first_move,
  };
  // Branching on the discriminant, not on whether tile copy happens to be
  // present: the pathway decides the field set, so a caller cannot widen the
  // base disclosure by passing something extra.
  return source === 'journey'
    ? {
        source,
        practice_id: input.practiceId,
        ...signed,
        title: input.tileCopy.title,
        hook: input.tileCopy.hook,
      }
    : { source, practice_id: arm.practice_id, ...signed };
}
